import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, ShieldOff, Lock } from "lucide-react";
import { SubTabs, Card, Pill, Toggle, Loading, ErrorState, ConfirmModal, KV } from "./AdminUI";

const TABS = [
  { key: "flags", label: "Feature Flags" },
  { key: "security", label: "Admin Security" },
];

export default function SettingsTab({ initialTab }) {
  const [tab, setTab] = useState(initialTab === "security" ? "security" : "flags");
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  return (
    <div data-testid="admin-settings-tab">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} testidPrefix="settings-tab" />
      {tab === "flags" ? <FlagsPanel /> : <SecurityPanel />}
    </div>
  );
}

const GROUPS = [
  ["operational", "Operational kill switches",
    "Emergency controls. Turning one off blocks the new action server-side with a clear message — nothing is ever hidden, downgraded or deleted."],
  ["program", "Membership programs",
    "Controls the Pro Plus commercial surface. Pausing never cancels or downgrades an existing subscriber."],
  ["display", "Display",
    "Cosmetic, site-wide visibility toggles. Underlying data keeps being computed."],
];

function FlagsPanel() {
  const [flags, setFlags] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setError(null);
    api.get("/admin/flags").then(r => setFlags(r.data.flags))
      .catch(e => setError(e.response?.data?.detail || e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = async (flag, next, reason) => {
    try {
      const r = await api.put(`/admin/flags/${flag.key}`, { enabled: next, reason });
      setFlags(r.data.flags);
      toast.success(`${flag.label} ${next ? "enabled" : "disabled"} — recorded in the audit log`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!flags) return <Loading />;

  return (
    <div className="space-y-6" data-testid="admin-flags-panel">
      {GROUPS.map(([group, title, blurb]) => {
        const rows = flags.filter(f => f.group === group);
        if (!rows.length) return null;
        return (
          <section key={group}>
            <h3 className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>{title}</h3>
            <p className="text-xs text-[#222]/55 mb-3 max-w-2xl">{blurb}</p>
            <div className="space-y-2">
              {rows.map(f => (
                <div key={f.key} className="bg-white rounded-2xl border border-black/5 p-4 flex items-start gap-4"
                  data-testid={`flag-${f.key}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold">{f.label}</p>
                      <Pill tone={f.enabled ? "green" : "red"}>{f.enabled ? "On" : "Off"}</Pill>
                      {f.locked && <Pill tone="neutral"><Lock size={9} className="inline mr-0.5" />Locked</Pill>}
                    </div>
                    <p className="text-xs text-[#222]/60 mt-1">{f.description}</p>
                    {f.locked && <p className="text-xs text-amber-700 mt-1">{f.locked_reason}</p>}
                    {f.extra && (
                      <div className="mt-2">
                        <KV label="Window" value={`${f.extra.start_date || "—"} → ${f.extra.end_date || "—"}`} />
                        <KV label="Active right now" value={f.extra.active_now ? "Yes" : "No"} />
                      </div>
                    )}
                  </div>
                  <Toggle on={f.enabled} disabled={f.locked}
                    onClick={() => setModal({ flag: f, next: !f.enabled })}
                    testid={`flag-toggle-${f.key}`} />
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {modal && (
        <ConfirmModal
          title={`${modal.next ? "Enable" : "Disable"} ${modal.flag.label}?`}
          impact={modal.flag.impact + "\n\nThis change takes effect immediately for everyone and is written to the audit log with your reason."}
          confirmLabel={modal.next ? "Enable" : "Disable"}
          danger={!modal.next}
          onConfirm={r => apply(modal.flag, modal.next, r)}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}

function SecurityPanel() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    api.get("/admin/audit", { params: { limit: 40 } }).then(r => setAudit(r.data)).catch(() => {});
  }, []);

  const startSetup = async () => {
    try { const r = await api.post("/auth/mfa/setup"); setSetup(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code });
      toast.success("MFA enabled — you'll need your authenticator app next sign-in.");
      setSetup(null); setCode(""); refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Incorrect code"); }
    setBusy(false);
  };
  const disable = async () => {
    const c = window.prompt("Enter your current 6-digit authenticator code to disable MFA:");
    if (!c) return;
    try { await api.post("/auth/mfa/disable", { code: c }); toast.success("MFA disabled"); refresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Incorrect code"); }
  };

  const securityActions = (audit?.entries || []).filter(
    e => ["SESSIONS_REVOKED", "FEATURE_FLAG_CHANGED", "EXPORT_GENERATED", "LEGAL_NOTICE_SENT"].includes(e.action));

  return (
    <div className="space-y-5" data-testid="admin-security-panel">
      <Card title="Two-factor authentication" testid="admin-mfa-card">
        {user?.mfa_enabled ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#245544]" /> MFA is <b>enabled</b> on this account.
            </p>
            <button onClick={disable} className="text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-700 font-semibold"
              data-testid="admin-mfa-disable">Disable MFA</button>
          </div>
        ) : setup ? (
          <div>
            <p className="text-sm text-[#222]/65 mb-3">
              Scan this QR code with Google Authenticator, Authy or any TOTP app, then enter the 6-digit code.
            </p>
            <img src={setup.qr_code} alt="MFA QR code" className="w-40 h-40 mb-3" data-testid="admin-mfa-qr" />
            <p className="text-xs text-[#222]/45 mb-3">Manual entry key: <code>{setup.secret}</code></p>
            <div className="flex gap-2 max-w-xs">
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                placeholder="000000" className="flex-1 px-3 py-2 border border-black/10 rounded-xl text-center font-mono"
                data-testid="admin-mfa-code-input" />
              <button onClick={enable} disabled={busy || code.length !== 6}
                className="px-4 py-2 text-sm rounded-full bg-[#2E6B57] text-white font-semibold disabled:opacity-50"
                data-testid="admin-mfa-enable-confirm">Confirm</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm flex items-center gap-2">
              <ShieldOff size={16} className="text-amber-600" /> MFA is <b>not enabled</b>. This is a launch blocker.
            </p>
            <button onClick={startSetup} className="px-4 py-2 text-sm rounded-full bg-[#2E6B57] text-white font-semibold"
              data-testid="admin-mfa-start-setup">Set up MFA</button>
          </div>
        )}
      </Card>

      <Card title="Authentication & access model" testid="admin-auth-model">
        <KV label="Admin password" value="Stored only as a bcrypt hash; the plaintext lives solely in backend secrets" />
        <KV label="Sessions" value="Signed JWT pinned to the account's session generation — revocable per account" />
        <KV label="Session revocation" value="Available per account from Users → Manage → Revoke sessions" />
        <KV label="Roles" value="Single `admin` role today; every admin endpoint is gated by require_admin" />
        <KV label="Multi-admin readiness" value="Audit entries already record actor id/email, so adding scoped admin roles needs no schema change" />
        <KV label="Impersonation" value="Not implemented — there is deliberately no 'log in as user' capability" />
      </Card>

      <Card title="What admin can and cannot see" testid="admin-privacy-model">
        <p className="text-xs font-semibold text-[#222]/70 mb-1">Visible</p>
        <p className="text-xs text-[#222]/60 mb-3">
          Account identity and status, membership/trial state, public content, reported content, moderation and
          audit history, activity counts, billing status via Stripe, email delivery metadata.
        </p>
        <p className="text-xs font-semibold text-[#222]/70 mb-1">Never exposed</p>
        <p className="text-xs text-[#222]/60">
          Password hashes, MFA secrets, session tokens, verification/reset tokens, card or payment credentials,
          direct messages, private My Board notes, private Project material, and exact client addresses.
        </p>
      </Card>

      <Card title="Recent security-sensitive actions" testid="admin-security-actions">
        {securityActions.length === 0 ? (
          <p className="text-xs text-[#222]/50">No session revocations, flag changes, exports or legal notices logged yet.</p>
        ) : (
          <div className="space-y-1.5">
            {securityActions.map(e => (
              <div key={e.id} className="text-xs border-b border-black/5 pb-1.5 last:border-0">
                <b>{e.action}</b> — {e.target_label || e.target_id} · {e.reason || "no reason"}
                <span className="text-[#222]/40"> · {new Date(e.created_at).toLocaleString()} · {e.admin_email}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
